/**
 * The move library: eighty curated harmonic moves, transposition-invariant.
 *
 * ## What this is
 *
 * The owner's brief, in his words: "i love the way it suggests chords with different
 * descriptors (eg dark and balanced, or warm and open). i want to keep that for sure …
 * could this be done key agnostic, so we build something through a mixture of precached
 * scenarios and logic to suggest patterns?" This file is the precached half. Nothing in
 * it names a pitch: every move is an **interval** above a reference the caller supplies,
 * so the same eighty records serve C major and G♭ minor identically, and a bug in
 * one key is a bug in all twelve — which is the only kind of bug worth having.
 *
 * ## Two rules that keep it honest
 *
 * 1. **A move is data.** No functions, no conditionals. Where a reading depends on the
 *    chord under the hands, that is `worksFrom`; where it depends on the key, that is
 *    `from: 'key'` plus `needsKeyMode`. So "the relative" is two records — the relative
 *    minor of a major chord and the relative major of a minor one — rather than one
 *    record with a mode switch inside it. Two records can be walked and checked; a
 *    switch has to be executed to be understood.
 * 2. **The same chord may appear under two kinds, and that is the point.** A♭ from C is
 *    a chromatic mediant and a borrowed ♭VI; both are true, they explain it differently,
 *    and which one the player is offered depends on which direction the palette drew.
 *    Deduplication happens on the *set* (one pitch-class set per branch set), never on
 *    the library.
 *
 * ## The voice
 *
 * Sensory first, theory second. Lowercase character words. No exclamation marks, and
 * none of the words in `BANNED_WORDS` — an explanation that calls a chord beautiful has
 * stopped describing it. `library.test.ts` walks every string in this file and enforces
 * all of that, plus the schema's length limits, because a phrase bank is exactly the
 * kind of thing that degrades one well-meant edit at a time.
 */

import type { HeuristicMove } from './types.js';

/**
 * Words this library does not use.
 *
 * Not a style preference: each of these describes the writer's reaction rather than the
 * sound, and a suggestion card has one line in which to tell a player what a chord will
 * do to their piece. "Very" is here for a different reason — it is the word that appears
 * when a description has not been rewritten.
 */
export const BANNED_WORDS: readonly string[] = Object.freeze([
  'beautiful',
  'beautifully',
  'amazing',
  'gorgeous',
  'stunning',
  'perfect',
  'very',
  'really',
  'awesome',
]);

/**
 * Every move, grouped by movement kind and roughly ordered tame → wild within each group.
 *
 * `band` is the adventurousness window in which a move is offered at all. It is a
 * judgement, and the numbers matter less than their ordering: a dominant is available at
 * every setting a doodler is likely to sit at, a tritone substitution is not available to
 * somebody who has asked for the obvious, and nothing at all is available at every
 * setting, because a move that is always eligible is a move with no opinion.
 */
export const MOVE_LIBRARY: readonly HeuristicMove[] = Object.freeze<HeuristicMove[]>([
  // ---------------------------------------------------------------------------
  // functional-dominant — the move that simply works.
  // ---------------------------------------------------------------------------
  {
    id: 'dominant-seventh-of-this',
    movementKind: 'functional-dominant',
    from: 'root',
    semitones: 7,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    band: [0, 0.45],
    phrases: {
      relationLabels: ['dominant of this chord', 'a fifth up, with a 7th', 'its own dominant'],
      descriptions: [
        'The chord a fifth above with a seventh on it. It leans hard back to where you already are.',
        'A fifth up, carrying the one note that will not settle. The most direct pull there is.',
        'Sets up a return: the tritone inside it points straight back at the chord under your hands.',
      ],
      characters: ['expectant', 'clear and expectant', 'taut'],
      styleTags: ['classic', 'resolving', 'pop'],
      explanation:
        '{to} is the dominant of {from} — {motion} from it, with a seventh that will not sit still. It {sharedPhrase}, and {newPhrase}. What it wants next is {from} itself, so this is the move to take when you want to come straight back to where you are.',
    },
  },
  {
    id: 'dominant-triad-of-this',
    movementKind: 'functional-dominant',
    from: 'root',
    semitones: 7,
    quality: 'major',
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    band: [0, 0.35],
    phrases: {
      relationLabels: ['the fifth above', 'plain dominant', 'a fifth up'],
      descriptions: [
        'A plain major chord a fifth above. Open rather than urgent, and it can go anywhere next.',
        'The dominant without its seventh: the pull is still there, but it is a suggestion, not a shove.',
        'Bright and uncluttered. Three notes that lift the harmony without committing to a landing.',
      ],
      characters: ['open', 'bright and steady', 'poised'],
      styleTags: ['folk', 'classic', 'open'],
      explanation:
        '{to} sits {motion} from {from}, the plainest step in tonal harmony. It {sharedPhrase}, and {newPhrase}. Without a seventh it is not obliged to resolve, so it will sit happily under a melody before dropping back to {from}.',
    },
  },
  {
    id: 'key-dominant-seventh',
    movementKind: 'functional-dominant',
    from: 'key',
    semitones: 7,
    quality: 'major',
    seventh: 'min7',
    needsKey: true,
    band: [0, 0.4],
    phrases: {
      relationLabels: ['the dominant of the key', 'the key’s own dominant', 'home dominant'],
      descriptions: [
        'The dominant of the key, whatever chord you are on now. It aims at the tonic.',
        'Points at the middle of the key, wherever in it you happen to be standing.',
        'The gathering chord: everything the key has been circling arrives after this one.',
      ],
      characters: ['gathering', 'clear', 'settled and tense'],
      styleTags: ['cadential', 'classic', 'pop'],
      explanation:
        '{to} is the dominant of {key}, so it aims at the middle of the key whatever chord it follows. It {sharedPhrase}, and {newPhrase}. Play the tonic after it and the phrase closes; play anything else and the close becomes a question.',
    },
  },
  {
    id: 'suspended-dominant',
    movementKind: 'functional-dominant',
    from: 'root',
    semitones: 7,
    quality: 'sus4',
    seventh: 'min7',
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    band: [0.15, 0.6],
    phrases: {
      relationLabels: ['dominant, suspended', 'a fifth up, unresolved', 'the softened dominant'],
      descriptions: [
        'The dominant with its third held back. All of the motion, none of the edge.',
        'A fourth stands in for the third, so the chord leans without pointing anywhere sharp.',
        'Hovering rather than driving. It can resolve, or it can just keep hanging there.',
      ],
      characters: ['hovering', 'soft and open', 'unhurried'],
      styleTags: ['pop', 'gospel', 'ambient'],
      explanation:
        '{to} is the dominant of {from} with a fourth where the third would be, which is what takes the sharpness out of it. It {sharedPhrase}, and {newPhrase}. It will drop into the plain dominant, or back to {from}, or simply stay — a suspension is only tense if you treat it as one.',
    },
  },
  {
    id: 'dominant-over-its-third',
    movementKind: 'functional-dominant',
    from: 'root',
    semitones: 7,
    quality: 'major',
    seventh: 'min7',
    bassSemitones: 4,
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    band: [0.1, 0.5],
    phrases: {
      relationLabels: ['dominant, third in bass', 'a fifth up, inverted', 'dominant over its 3rd'],
      descriptions: [
        'The dominant with its third underneath, so the bass steps rather than leaps on the way back.',
        'Same pull, smoother floor: the bass note sits a semitone under where it is heading.',
        'An inversion that keeps the bass line walking instead of jumping around.',
      ],
      characters: ['smooth', 'walking', 'poised'],
      styleTags: ['classic', 'lyrical', 'pop'],
      voicingHint: 'Keep the bass note alone at the bottom — the inversion is the whole point.',
      explanation:
        '{to} is the dominant of {from} turned onto its third, so the lowest note is a semitone below the root of {from}. It {sharedPhrase}, and {newPhrase}. The bass now steps into the return rather than dropping a fifth, which is the difference between a cadence and a line.',
    },
  },
  {
    id: 'leading-tone-diminished-seventh',
    movementKind: 'functional-dominant',
    from: 'root',
    semitones: 11,
    quality: 'diminished',
    seventh: 'dim7',
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['leading-tone diminished', 'a semitone below', 'the diminished approach'],
      descriptions: [
        'A diminished seventh a semitone below. Every note of it wants to move, and its root rises into yours.',
        'Tension with no root of its own. It exists to lean into the chord you are already on.',
        'Four notes stacked in minor thirds, hanging a semitone under the chord you are on and pressing upward.',
      ],
      characters: ['coiled', 'dark and tense', 'restless'],
      styleTags: ['classical', 'cinematic', 'romantic'],
      explanation:
        '{to} sits a semitone below {from} and is built entirely of minor thirds, so it has no comfortable resting point of its own. It {sharedPhrase}, and {newPhrase}. Almost anything after it sounds like a release; {from} sounds like the intended one.',
    },
  },

  // ---------------------------------------------------------------------------
  // functional-subdominant — opening out rather than resolving.
  // ---------------------------------------------------------------------------
  {
    id: 'fourth-above-major',
    movementKind: 'functional-subdominant',
    from: 'root',
    semitones: 5,
    quality: 'major',
    worksFrom: ['major', 'dominant', 'suspended', 'augmented'],
    band: [0, 0.35],
    phrases: {
      relationLabels: ['a fourth above', 'up a fourth', 'the root falls a fifth'],
      descriptions: [
        'A plain major chord a fourth up. The root falls a fifth, the strongest step there is.',
        'From a dominant this is where it lands; from anything else it opens the sound out.',
        'Broad and unhurried, with nothing in it under pressure. Easy to keep going from.',
      ],
      characters: ['open', 'warm and open', 'broad'],
      styleTags: ['pop', 'folk', 'diatonic'],
      explanation:
        '{to} is {motion} from {from}, so the root falls a fifth, the strongest root motion in tonal harmony. It {sharedPhrase}, and {newPhrase}. Nothing in {to} is under pressure, so it will lead on to a dominant, back to {from}, or sideways into its own relative.',
    },
  },
  {
    id: 'fourth-above-minor',
    movementKind: 'functional-subdominant',
    from: 'root',
    semitones: 5,
    quality: 'minor',
    worksFrom: ['minor', 'diminished'],
    band: [0, 0.4],
    phrases: {
      relationLabels: ['minor a fourth above', 'down a fifth, minor', 'up a fourth, minor'],
      descriptions: [
        'A minor chord a fourth up. It opens the harmony without letting any light into it.',
        'Up a fourth, kept in the shade. Wider, but no brighter.',
        'Steps outward and stays inward at the same time.',
      ],
      characters: ['inward', 'dark and open', 'grave'],
      styleTags: ['modal', 'cinematic', 'folk'],
      explanation:
        '{to} is {motion} from {from} and is minor, so the harmony opens out without changing its light. It {sharedPhrase}, and {newPhrase}. From here a dominant is easy to reach, and so is the relative major of {to} if you want the room to brighten.',
    },
  },
  {
    id: 'fourth-above-major-seventh',
    movementKind: 'functional-subdominant',
    from: 'root',
    semitones: 5,
    quality: 'major',
    seventh: 'maj7',
    // Not from a minor chord. Am → Dmaj7 brings C♯ in against C and F♯ against F — a
    // Dorian brightening, which `dorian-major-fourth` offers higher up the slider with
    // prose that says so. Here, at the timid end, it would be wearing "soft-edged".
    worksFrom: ['major', 'dominant', 'suspended'],
    band: [0.05, 0.5],
    phrases: {
      relationLabels: ['major 7th a fourth up', 'a fourth up, softened', 'a fourth up, with a 7th'],
      descriptions: [
        'A fourth up with a major seventh on top. Soft-edged, and in no hurry to be anywhere.',
        'A fourth up with a note added that blurs its outline rather than sharpening it.',
        'Wide and slightly hazy. It suits a hand that wants to sit still for a bar.',
      ],
      characters: ['hazy', 'warm and still', 'plush'],
      styleTags: ['jazz', 'soul', 'ambient'],
      explanation:
        '{to} is {motion} from {from} with a major seventh added, the note that softens its edges. It {sharedPhrase}, and {newPhrase}. It sits comfortably for as long as you leave it, and steps on to a dominant whenever you want to move.',
    },
  },
  {
    id: 'key-supertonic-seventh',
    movementKind: 'functional-subdominant',
    from: 'key',
    semitones: 2,
    quality: 'minor',
    seventh: 'min7',
    needsKeyMode: 'major',
    band: [0.05, 0.5],
    phrases: {
      relationLabels: ['the second degree, minor', 'ii7 of the key', 'the approach chord'],
      descriptions: [
        'The minor seventh on the second degree — the chord that walks into the dominant.',
        'The first half of a ii–V, the most travelled road in jazz. It exists to be followed.',
        'Gentle on its own, and pointed the moment a dominant arrives after it.',
      ],
      characters: ['leaning', 'soft and directed', 'pliant'],
      styleTags: ['jazz', 'pop', 'diatonic'],
      explanation:
        '{to} is built on the second degree of {key}, which makes it the standard approach to that key’s dominant. It {sharedPhrase}, and {newPhrase}. Play the dominant next and you have the phrase every listener already knows; play something else and the expectation is yours to spend.',
    },
  },
  {
    id: 'key-half-diminished-supertonic',
    movementKind: 'functional-subdominant',
    from: 'key',
    semitones: 2,
    quality: 'diminished',
    seventh: 'min7',
    needsKeyMode: 'minor',
    band: [0.2, 0.65],
    phrases: {
      relationLabels: ['half-diminished second', 'the minor-key approach', 'a shaded ii'],
      descriptions: [
        'The second degree of a minor key, with a flattened fifth. Unsteady in a useful way.',
        'Half-diminished: not the full darkness, but nowhere near solid ground either.',
        'It tilts. Follow it with the dominant and the tilt becomes a direction.',
      ],
      characters: ['tilted', 'dark and unsteady', 'searching'],
      styleTags: ['jazz', 'cinematic', 'classical'],
      explanation:
        '{to} sits on the second degree of {key} with a lowered fifth, which is what a minor key gives you instead of a plain approach chord. It {sharedPhrase}, and {newPhrase}. The dominant is the expected next step, and the pair together is the sound of a minor cadence gathering.',
    },
  },
  {
    id: 'fourth-over-the-fifth',
    movementKind: 'functional-subdominant',
    from: 'root',
    semitones: 5,
    quality: 'major',
    bassSemitones: 7,
    band: [0.25, 0.7],
    phrases: {
      relationLabels: [
        'a fourth up, over its 5th',
        'a fourth up, on a pedal',
        'the fourth, held down',
      ],
      descriptions: [
        'A fourth up, over the root of the chord you are on, so the floor can stay put.',
        'The chord changes above the root you were standing on, so it reads as colour rather than travel.',
        'Same footing, new light. With that root held in the bass, nothing underneath has to shift.',
      ],
      characters: ['suspended', 'wide and grounded', 'floating'],
      styleTags: ['film', 'ambient', 'gospel'],
      voicingHint: 'Keep the old root alone in the bass and let the right hand do the moving.',
      explanation:
        '{to} puts the chord {motion} from {from} over the root of {from}, so with that root in the bass nothing underneath has to move. It {sharedPhrase}, and {newPhrase}. With the floor unchanged the ear hears a widening rather than a step, which is why this works under a held melody note.',
    },
  },

  // ---------------------------------------------------------------------------
  // relative — the shared-key-signature neighbour.
  // ---------------------------------------------------------------------------
  {
    id: 'relative-minor',
    movementKind: 'relative',
    from: 'root',
    semitones: 9,
    quality: 'minor',
    worksFrom: ['major', 'dominant'],
    avoidAfterKinds: ['relative'],
    band: [0.05, 0.5],
    phrases: {
      relationLabels: ['the relative minor', 'a minor third below', 'its shadow chord'],
      descriptions: [
        'Shares most of its notes with where you are, but changes the light on all of them.',
        'The same key signature seen from underneath. Familiar notes, different weather.',
        'Two of three notes stay put. The one that moves is the one carrying the mood.',
      ],
      characters: ['shaded', 'soft and inward', 'wistful'],
      styleTags: ['pop', 'lyrical', 'diatonic'],
      explanation:
        '{to} is the relative minor of {from} — {motion}, and two of its three notes are already in {from}. It {sharedPhrase}, and {newPhrase}. Almost nothing has changed on paper, which is why the change in mood is so much larger than the change in notes.',
    },
  },
  {
    id: 'relative-major',
    movementKind: 'relative',
    from: 'root',
    semitones: 3,
    quality: 'major',
    worksFrom: ['minor'],
    avoidAfterKinds: ['relative'],
    band: [0.05, 0.5],
    phrases: {
      relationLabels: ['the relative major', 'a minor third above', 'the brighter neighbour'],
      descriptions: [
        'The major chord that shares this key signature. Warmer, without leaving the room.',
        'Lifts the light on notes you are already holding. The sadness stays underneath.',
        'A minor third up, and suddenly the same material reads as open rather than closed.',
      ],
      characters: ['warm', 'warm and open', 'lifted'],
      styleTags: ['pop', 'lyrical', 'diatonic'],
      explanation:
        '{to} is the relative major of {from} — {motion}, on the same key signature. It {sharedPhrase}, and {newPhrase}. It is the cheapest brightening in tonal music: one note moves and the whole chord changes character.',
    },
  },
  {
    id: 'relative-minor-seventh',
    movementKind: 'relative',
    from: 'root',
    semitones: 9,
    quality: 'minor',
    seventh: 'min7',
    worksFrom: ['major', 'dominant'],
    band: [0.1, 0.55],
    phrases: {
      relationLabels: ['relative minor, with a 7th', 'the shadow, extended', 'minor 7th below'],
      descriptions: [
        'The relative minor with a seventh, so the whole of the major triad survives inside it.',
        'The major triad stays inside it. One note is added underneath and the colour turns.',
        'Softer than the plain relative minor, and it flows on rather than settling.',
      ],
      characters: ['mellow', 'soft and shaded', 'flowing'],
      styleTags: ['jazz', 'soul', 'pop'],
      explanation:
        '{to} is the relative minor of {from} with a seventh added, and the triad of {from} sits whole inside it. It {sharedPhrase}, and {newPhrase}. Because that triad survives the change, it reads as a re-lighting rather than a move.',
    },
  },
  {
    id: 'relative-major-seventh',
    movementKind: 'relative',
    from: 'root',
    semitones: 3,
    quality: 'major',
    seventh: 'maj7',
    worksFrom: ['minor'],
    band: [0.1, 0.55],
    phrases: {
      relationLabels: [
        'relative major, with a 7th',
        'the brighter neighbour, soft',
        'major 7th above',
      ],
      descriptions: [
        'The relative major with a seventh on it — brighter, but blurred at the edges.',
        'Opens the harmony upward while keeping a note of the minor chord underneath it.',
        'Warm and slightly unresolved, which is a good place to leave a phrase hanging.',
      ],
      characters: ['glowing', 'warm and hazy', 'open'],
      styleTags: ['jazz', 'lyrical', 'ambient'],
      explanation:
        '{to} is the relative major of {from} with a major seventh on top, which blurs its edges. It {sharedPhrase}, and {newPhrase}. The added seventh keeps it from sounding like an arrival, so it brightens without closing anything off.',
    },
  },
  {
    id: 'key-mediant-minor',
    movementKind: 'relative',
    from: 'key',
    semitones: 4,
    quality: 'minor',
    needsKeyMode: 'major',
    band: [0.15, 0.6],
    phrases: {
      relationLabels: ['the third degree, minor', 'the mediant', 'iii of the key'],
      descriptions: [
        'The quiet minor chord in the middle of a major key. Rarely used, and always noticed.',
        'It sits between the tonic and the dominant and refuses to behave like either.',
        'Neutral, faintly cool, and unusually good at delaying whatever comes next.',
      ],
      characters: ['cool', 'quiet and neutral', 'suspended'],
      styleTags: ['classical', 'lyrical', 'diatonic'],
      explanation:
        '{to} is the mediant of {key}, the minor chord on its third degree. It {sharedPhrase}, and {newPhrase}. It shares notes with both the tonic and the dominant without acting as either, which is why it stalls a phrase so effectively.',
    },
  },

  // ---------------------------------------------------------------------------
  // parallel — the same root, the other mode.
  // ---------------------------------------------------------------------------
  {
    id: 'parallel-minor',
    movementKind: 'parallel',
    from: 'root',
    semitones: 0,
    quality: 'minor',
    // Not from a dominant: G7 → Gm offers the chord at the cadence point its own darkened
    // twin and drops the seventh on the way. `parallel-minor-seventh` is the musical
    // version of that move — G7 → Gm7, the ii of the IV.
    worksFrom: ['major'],
    avoidAfterKinds: ['parallel'],
    band: [0.05, 0.6],
    phrases: {
      relationLabels: ['same root, minor', 'the parallel minor', 'the third, lowered'],
      descriptions: [
        'The same root with the third flipped down. One note moves and the mood turns over.',
        'Nothing travels. The root and the fifth stay, and the middle of the chord darkens.',
        'The cheapest change of light there is: a semitone, in the one place it counts.',
      ],
      characters: ['turned', 'dark and intimate', 'dusky'],
      styleTags: ['pop', 'cinematic', 'modal'],
      explanation:
        '{to} keeps the root of {from} and lowers its third, so the harmony does not travel at all. It {sharedPhrase}, and {newPhrase}. A single semitone does the whole job, which is why this reads as a change of weather rather than a change of place.',
    },
  },
  {
    id: 'parallel-major',
    movementKind: 'parallel',
    from: 'root',
    semitones: 0,
    quality: 'major',
    worksFrom: ['minor'],
    avoidAfterKinds: ['parallel'],
    band: [0.05, 0.6],
    phrases: {
      relationLabels: ['same root, major', 'the parallel major', 'the third, raised'],
      descriptions: [
        'The same root with the third lifted. The floor is unchanged and the room is brighter.',
        'One note rises by a semitone and the chord stops being sad about itself.',
        'A shaft of light through the same harmony. Nothing else has to move for it.',
      ],
      characters: ['lifted', 'bright and steady', 'tender'],
      styleTags: ['pop', 'classical', 'lyrical'],
      explanation:
        '{to} keeps the root of {from} and raises its third, which is the smallest change that can turn a minor chord major. It {sharedPhrase}, and {newPhrase}. Coming after a minor chord on the same root it always sounds like a decision rather than a drift.',
    },
  },
  {
    id: 'parallel-minor-seventh',
    movementKind: 'parallel',
    from: 'root',
    semitones: 0,
    quality: 'minor',
    seventh: 'min7',
    worksFrom: ['major', 'dominant'],
    band: [0.15, 0.65],
    phrases: {
      relationLabels: ['same root, minor 7th', 'parallel minor, extended', 'darkened, with a 7th'],
      descriptions: [
        'The parallel minor with a seventh, which softens the turn and keeps it moving.',
        'Darker and looser at once. It sounds like the start of somewhere rather than an arrival.',
        'The same root, the mood inverted, and a seventh that refuses to close the door.',
      ],
      characters: ['smoky', 'dark and loose', 'mellow'],
      styleTags: ['jazz', 'soul', 'cinematic'],
      explanation:
        '{to} keeps the root of {from}, lowers its third and has a minor seventh on top. It {sharedPhrase}, and {newPhrase}. The seventh is what stops this feeling like a full stop — it turns the darkening into a corridor rather than a room.',
    },
  },
  {
    id: 'parallel-major-seventh',
    movementKind: 'parallel',
    from: 'root',
    semitones: 0,
    quality: 'major',
    seventh: 'maj7',
    worksFrom: ['minor'],
    band: [0.15, 0.65],
    phrases: {
      relationLabels: ['same root, major 7th', 'parallel major, soft', 'brightened, with a 7th'],
      descriptions: [
        'The parallel major with a seventh: brighter, but hazed over rather than blazing.',
        'The third rises and a major seventh sits on top, so the lift comes with something still unresolved.',
        'Warm without being final. A good chord to change your mind on top of.',
      ],
      characters: ['luminous', 'warm and hazy', 'open'],
      styleTags: ['jazz', 'ambient', 'lyrical'],
      explanation:
        '{to} keeps the root of {from}, raises its third and puts a major seventh on top. It {sharedPhrase}, and {newPhrase}. The seventh keeps a trace of tension, so the brightening does not read as a resolution.',
    },
  },
  {
    id: 'parallel-minor-sixth',
    movementKind: 'parallel',
    from: 'root',
    semitones: 0,
    quality: 'minor',
    extensions: ['6'],
    worksFrom: ['major', 'dominant', 'suspended'],
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['same root, minor 6th', 'the minor with a 6th', 'darkened and lifted'],
      descriptions: [
        'Minor on the same root, with a sixth that keeps one foot in the light.',
        'A minor chord that has not quite committed. The added note pulls against the third.',
        'Velvety and slightly ambiguous, and it moves on in almost any direction.',
      ],
      characters: ['velvety', 'dark and lifted', 'ambiguous'],
      styleTags: ['jazz', 'film', 'romantic'],
      ambiguityNote: 'The same four notes read as a half-diminished seventh built on the sixth.',
      explanation:
        '{to} takes the root of {from} minor and adds a sixth, which is the note that stops the chord settling into plain sadness. It {sharedPhrase}, and {newPhrase}. Those four notes are also a half-diminished seventh built on its sixth, a minor third below the root, so where you go next decides which chord it was.',
    },
  },

  // ---------------------------------------------------------------------------
  // modal — a chord from a mode other than the prevailing one.
  // ---------------------------------------------------------------------------
  {
    id: 'mixolydian-flat-seventh',
    movementKind: 'modal',
    from: 'root',
    semitones: 10,
    quality: 'major',
    worksFrom: ['major', 'dominant', 'suspended'],
    band: [0.25, 0.7],
    phrases: {
      relationLabels: ['a whole tone below', 'down a tone, major', 'the Mixolydian step'],
      descriptions: [
        'A major chord a whole tone down. It sounds folk-ish or rock-ish, and rarely tense.',
        'A whole tone down, with no leading note into where you were. It loosens rather than pulls.',
        'Plain, wide and slightly old. It leans outward rather than back.',
      ],
      characters: ['plain and wide', 'buoyant', 'weathered'],
      styleTags: ['rock', 'folk', 'modal'],
      explanation:
        '{to} is {motion} from {from}, the major chord a Mixolydian scale on {from} has on its seventh degree. It {sharedPhrase}, and {newPhrase}. Nothing in it leads back into {from}, so the harmony can wander instead of resolving.',
    },
  },
  {
    id: 'dorian-major-fourth',
    movementKind: 'modal',
    from: 'root',
    semitones: 5,
    quality: 'major',
    worksFrom: ['minor'],
    band: [0.25, 0.7],
    phrases: {
      relationLabels: [
        'major a fourth above',
        'up a fourth, brightened',
        'the Dorian-coloured fourth',
      ],
      descriptions: [
        'A major chord a fourth above a minor one. The raised sixth opens the whole mode out.',
        'The one note that separates Dorian from plain minor, and it arrives in this chord.',
        'Buoyant against a minor chord. Not brighter exactly — airier.',
      ],
      characters: ['airy', 'open and buoyant', 'cool'],
      styleTags: ['modal', 'jazz', 'folk'],
      explanation:
        '{to} is a major chord {motion} from the minor {from}, so it carries the note a major sixth above the root of {from} — the note a natural-minor scale on {from} would lower. It {sharedPhrase}, and {newPhrase}. Heard against {from}, that one note is the Dorian colour; in a major key the same pair is simply ii going to V.',
    },
  },
  {
    id: 'dorian-sixth',
    movementKind: 'modal',
    from: 'root',
    semitones: 0,
    quality: 'minor',
    extensions: ['6'],
    worksFrom: ['minor'],
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['the same chord, with a 6th', 'minor with a major 6th', 'the Dorian colour'],
      descriptions: [
        'The same minor triad with a sixth added. Minor, but with a window open somewhere.',
        'The raised sixth turns plain minor into something with far less urge to resolve.',
        'Same root, same third, and one added note that changes the whole mode underneath.',
      ],
      characters: ['open', 'cool and floating', 'unhurried'],
      styleTags: ['modal', 'jazz', 'film'],
      explanation:
        '{to} puts a major sixth on the minor triad of {from}. It {sharedPhrase}, and {newPhrase}. That one note is the difference between natural minor and Dorian on this root, and Dorian is the minor mode that does not ask to resolve.',
    },
  },
  {
    id: 'lydian-second',
    movementKind: 'modal',
    from: 'root',
    semitones: 2,
    quality: 'major',
    worksFrom: ['major', 'dominant'],
    band: [0.35, 0.8],
    phrases: {
      relationLabels: ['major a tone above', 'up a whole tone, major', 'the Lydian step'],
      descriptions: [
        'A major chord a whole tone up. It brings in a raised fourth and lifts the ceiling with it.',
        'Bright to the point of glare, with a note the chord below it does not have.',
        'Lifts without leaning back. The harmony gets taller.',
      ],
      characters: ['bright', 'clean and lifted', 'glassy'],
      styleTags: ['film', 'modal', 'pop'],
      explanation:
        '{to} is a major chord {motion} from {from}, and it holds the note a tritone above the root of {from} — the raised fourth that makes Lydian. It {sharedPhrase}, and {newPhrase}. Over {from} that note brightens without pulling back; in a key where {to} is the dominant it pulls hard instead, so what comes next decides which it was.',
    },
  },
  {
    id: 'phrygian-flat-second',
    movementKind: 'modal',
    from: 'root',
    semitones: 1,
    quality: 'major',
    worksFrom: ['minor'],
    band: [0.45, 0.9],
    phrases: {
      relationLabels: ['a semitone above', 'the Phrygian second', 'the Phrygian chord'],
      descriptions: [
        'A major chord one semitone up from a minor one. Old, severe, and immediately Spanish.',
        'The Phrygian second over a minor chord. It presses down onto the chord you are on.',
        'Bright notes over a dark implication, which is what makes this one hard to place.',
      ],
      characters: ['severe', 'dark and bright', 'antique'],
      styleTags: ['flamenco', 'modal', 'cinematic'],
      explanation:
        '{to} is a major chord a semitone above {from}, the lowered second that a Phrygian scale on {from} has. It {sharedPhrase}, and {newPhrase}. Falling back a semitone to {from} is the Phrygian cadence, and it is one of the oldest closing gestures there is.',
    },
  },
  {
    id: 'aeolian-flat-sixth',
    movementKind: 'modal',
    from: 'root',
    semitones: 8,
    quality: 'major',
    worksFrom: ['minor'],
    band: [0.25, 0.7],
    phrases: {
      relationLabels: [
        'major, a major 3rd down',
        'down a major third, major',
        'the wide chord a third below',
      ],
      descriptions: [
        'A major chord a major third below a minor one. Broad, and it lifts the mood as the bass drops.',
        'Warm light beside a minor chord: two notes stay and the bass drops a third.',
        'A wide major chord that shares two notes with where you are and changes the floor.',
      ],
      characters: ['broad', 'warm and grave', 'expansive'],
      styleTags: ['cinematic', 'modal', 'romantic'],
      explanation:
        '{to} is a major chord {motion} from {from}, the chord a natural-minor scale on {from} has on its sixth degree. It {sharedPhrase}, and {newPhrase}. Against a minor chord it sounds like the picture opening out, which is why film music reaches for it.',
    },
  },

  // ---------------------------------------------------------------------------
  // chromatic-mediant — a third away, chromatically, sharing one tone or none.
  // Every one of these is a reading rather than a fact, so none claims certainty.
  // ---------------------------------------------------------------------------
  {
    id: 'chromatic-major-third-up-major',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 4,
    quality: 'major',
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    interpretive: true,
    band: [0.5, 0.95],
    phrases: {
      relationLabels: ['a major third above', 'chromatic third up', 'the bright mediant'],
      descriptions: [
        'A major third up, with a chromatic root. Bright, and slightly unmoored from where you were.',
        'Third-related and not from the same scale, so it arrives without warning from the chord before it.',
        'A sudden widening of the light. Almost every note is new.',
      ],
      characters: ['lifted', 'bright and uncanny', 'expansive'],
      styleTags: ['cinematic', 'romantic', 'film'],
      ambiguityNote: 'Also hearable as the dominant of the chord a fourth above it.',
      explanation:
        '{to} sits {motion} from {from} with a chromatic root motion, which is what makes it a mediant rather than a step. It {sharedPhrase}, and {newPhrase}. Third relations like this do not resolve anywhere in particular, so treat the next chord as a fresh decision.',
    },
  },
  {
    id: 'chromatic-major-third-down-major',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 8,
    quality: 'major',
    worksFrom: ['major', 'dominant', 'suspended'],
    interpretive: true,
    band: [0.5, 0.95],
    phrases: {
      relationLabels: ['a major third below', 'chromatic third down', 'the flat mediant'],
      descriptions: [
        'A major third down, chromatically. Broad and cinematic, with one note held over.',
        'The harmony drops a third and widens rather than darkening. It is a scenery change.',
        'Familiar enough to follow and far enough to notice. An old film-music move.',
      ],
      characters: ['broad', 'lush and distant', 'grand'],
      styleTags: ['cinematic', 'film', 'romantic'],
      explanation:
        '{to} is {motion} from {from}, a chromatic third rather than a diatonic one. It {sharedPhrase}, and {newPhrase}. The shared note is what keeps it from sounding like a mistake.',
    },
  },
  {
    id: 'chromatic-minor-third-up-major',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 3,
    quality: 'major',
    worksFrom: ['major', 'dominant', 'suspended'],
    interpretive: true,
    band: [0.5, 0.9],
    phrases: {
      relationLabels: ['major a minor 3rd up', 'up a minor third, major', 'a sidestep up a third'],
      descriptions: [
        'A major chord a minor third above. Warm, sideways, and not from the same scale.',
        'The root climbs a minor third and the chord stays major, which no scale will give you.',
        'A sidestep that keeps the warmth and loses the bearings.',
      ],
      characters: ['warm and strange', 'sideways', 'cinematic'],
      styleTags: ['cinematic', 'rock', 'romantic'],
      explanation:
        '{to} is a major chord {motion} from {from}, a pair no single scale contains. It {sharedPhrase}, and {newPhrase}. It does not resolve anywhere in particular — it relocates the harmony a third away.',
    },
  },
  {
    id: 'chromatic-minor-third-down-major',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 9,
    quality: 'major',
    worksFrom: ['major', 'minor', 'dominant'],
    interpretive: true,
    band: [0.55, 1],
    phrases: {
      relationLabels: [
        'major a minor 3rd down',
        'down a minor third, major',
        'the bright third below',
      ],
      descriptions: [
        'A major chord a minor third below, with a raised third of its own. Sharp and clear.',
        'It drops a third and turns major where the scale would have made it minor. Brighter than where you were.',
        'Clean, unexpected light. It works best if you let it sit for a moment.',
      ],
      characters: ['clear and remote', 'bright', 'crisp'],
      styleTags: ['romantic', 'film', 'classical'],
      explanation:
        '{to} is {motion} from {from}, major where the major scale on the root of {from} would make it minor. It {sharedPhrase}, and {newPhrase}. Its third is also a strong leading tone, so this can be heard as a dominant if you follow it with the chord a fourth above.',
    },
  },
  {
    id: 'chromatic-major-third-up-minor',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 4,
    quality: 'minor',
    worksFrom: ['minor'],
    interpretive: true,
    band: [0.55, 1],
    phrases: {
      relationLabels: ['minor a major 3rd up', 'the dark mediant', 'chromatic third, minor'],
      descriptions: [
        'A minor chord a major third up. Cool, weightless, and hard to place.',
        'Third-related and still minor, so the shift happens without any change of light.',
        'It drifts rather than moves. Nothing in it points anywhere.',
      ],
      characters: ['weightless', 'cool and shadowed', 'drifting'],
      styleTags: ['ambient', 'cinematic', 'film'],
      explanation:
        '{to} is {motion} from {from} and stays minor, so its root lies outside {from}’s own scale while the mood stays identical. It {sharedPhrase}, and {newPhrase}. The result floats: neither chord has any claim on the other.',
    },
  },
  {
    id: 'chromatic-major-third-down-minor',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 8,
    quality: 'minor',
    worksFrom: ['major', 'minor', 'dominant'],
    interpretive: true,
    band: [0.6, 1],
    phrases: {
      relationLabels: [
        'minor a major 3rd down',
        'down a major third, minor',
        'the dark third below',
      ],
      descriptions: [
        'A minor chord a major third down. The floor drops out from under it.',
        'Dark and wide at once. The most abrupt of the third relations, and the most cinematic.',
        'The harmony falls a third and takes the light with it.',
      ],
      characters: ['dusky', 'dark and wide', 'sinking'],
      styleTags: ['cinematic', 'film', 'romantic'],
      explanation:
        '{to} is {motion} from {from} and minor, the darkest of the third relations. It {sharedPhrase}, and {newPhrase}. Heard as a third relation rather than as a move within a key, it lands as a cut.',
    },
  },
  {
    id: 'chromatic-minor-third-up-minor',
    movementKind: 'chromatic-mediant',
    from: 'root',
    semitones: 3,
    quality: 'minor',
    worksFrom: ['major', 'minor', 'dominant', 'suspended'],
    interpretive: true,
    band: [0.6, 1],
    phrases: {
      relationLabels: [
        'minor a minor 3rd up',
        'up a minor third, minor',
        'the shadowed third above',
      ],
      descriptions: [
        'A minor chord a minor third above. Close on paper and a long way off to the ear.',
        'It shares almost nothing with where you are, and the small root motion hides that.',
        'Shadowed and slightly airless. It rewards being left alone for a bar.',
      ],
      characters: ['shadowed', 'close and remote', 'airless'],
      styleTags: ['cinematic', 'ambient', 'experimental'],
      explanation:
        '{to} is a minor chord {motion} from {from}. It {sharedPhrase}, and {newPhrase}. A small step in the bass covering a large change in the harmony is exactly what makes chromatic mediants disorienting, and it is the effect to use them for.',
    },
  },

  // ---------------------------------------------------------------------------
  // borrowed — a chord from the parallel key.
  // ---------------------------------------------------------------------------
  {
    id: 'borrowed-flat-sixth',
    movementKind: 'borrowed',
    from: 'key',
    semitones: 8,
    quality: 'major',
    needsKeyMode: 'major',
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['the flat sixth', 'borrowed ♭VI', 'the sixth, lowered'],
      descriptions: [
        'A major chord borrowed from the parallel minor. Broad, and shaded rather than dark.',
        'Not from this key, but from its twin — which is why it sounds familiar and wrong at once.',
        'Wide and slightly grave. It opens the harmony downward instead of upward.',
      ],
      characters: ['grave and wide', 'shaded', 'expansive'],
      styleTags: ['cinematic', 'rock', 'romantic'],
      explanation:
        '{to} is the major chord on the flattened sixth of {key}, borrowed from its parallel minor. It {sharedPhrase}, and {newPhrase}. Borrowing rather than modulating is why it can leave again immediately without needing to be explained.',
    },
  },
  {
    id: 'borrowed-flat-seventh',
    movementKind: 'borrowed',
    from: 'key',
    semitones: 10,
    quality: 'major',
    needsKeyMode: 'major',
    band: [0.25, 0.7],
    phrases: {
      relationLabels: ['the flat seventh', 'borrowed ♭VII', 'the seventh, lowered'],
      descriptions: [
        'A major chord a whole tone below home, borrowed from the parallel minor. Plain and open.',
        'It removes the leading note, which takes the urgency out of everything that follows.',
        'Rock harmony in one chord: wide, unhurried, and not in a hurry to explain itself.',
      ],
      characters: ['plain and wide', 'unhurried', 'weathered'],
      styleTags: ['rock', 'folk', 'modal'],
      explanation:
        '{to} is the major chord on the flattened seventh of {key}, borrowed from the parallel minor. It {sharedPhrase}, and {newPhrase}. It has no leading note in it, which is why this chord turns up wherever a tonal centre is meant to feel optional.',
    },
  },
  {
    id: 'borrowed-minor-fourth',
    movementKind: 'borrowed',
    from: 'key',
    semitones: 5,
    quality: 'minor',
    needsKeyMode: 'major',
    band: [0.25, 0.7],
    phrases: {
      relationLabels: ['the minor four', 'borrowed iv', 'the subdominant, minor'],
      descriptions: [
        'The subdominant turned minor. One of the oldest ways to make a major key ache.',
        'The key’s fourth chord with its third lowered. It opens out and darkens in the same gesture.',
        'Tender and slightly bruised. Almost anything sounds like a homecoming after it.',
      ],
      characters: ['tender', 'dark and open', 'bruised'],
      styleTags: ['pop', 'romantic', 'gospel'],
      explanation:
        '{to} is the subdominant of {key} with its third lowered, borrowed from the parallel minor. It {sharedPhrase}, and {newPhrase}. It is the classic pre-cadence ache: resolve to the tonic after it and the arrival lands much harder than it would have.',
    },
  },
  {
    id: 'borrowed-minor-fourth-sixth',
    movementKind: 'borrowed',
    from: 'root',
    semitones: 5,
    quality: 'minor',
    extensions: ['6'],
    worksFrom: ['major', 'dominant'],
    band: [0.35, 0.8],
    phrases: {
      relationLabels: [
        'minor a fourth up, with a 6th',
        'up a fourth, minor 6th',
        'the shaded fourth',
      ],
      descriptions: [
        'A minor chord a fourth up with a sixth on it. Plush, wistful, and gently unresolved.',
        'One extra note on a minor chord, which is what turns the ache into a colour.',
        'Soft-focus and slightly suspended. It leaves the next move entirely open.',
      ],
      characters: ['plush', 'wistful', 'soft and shaded'],
      styleTags: ['jazz', 'soul', 'film'],
      explanation:
        '{to} is a minor chord {motion} from {from} with a sixth added. When {from} is home, this is the borrowed minor subdominant. It {sharedPhrase}, and {newPhrase}. The added note pulls against the minor third, which is why the chord sounds wistful rather than simply sad.',
    },
  },
  {
    id: 'borrowed-flat-sixth-major-seventh',
    movementKind: 'borrowed',
    from: 'key',
    semitones: 8,
    quality: 'major',
    seventh: 'maj7',
    needsKeyMode: 'major',
    band: [0.4, 0.85],
    phrases: {
      relationLabels: ['flat sixth, with a 7th', 'borrowed ♭VImaj7', 'the wide borrowed chord'],
      descriptions: [
        'The flat sixth borrowed from the parallel minor, with a major seventh on top. Velvet and wide.',
        'Wide and velvet, and the tonic is still inside it.',
        'Broad and unhurried: home seen through coloured glass.',
      ],
      characters: ['velvet', 'wide and warm', 'anchored'],
      styleTags: ['cinematic', 'jazz', 'film'],
      voicingHint: 'Keep the tonic on top and let the left hand find the new root.',
      explanation:
        '{to} is the flat sixth of {key} with a major seventh, borrowed from the parallel minor. It {sharedPhrase}, and {newPhrase}. Its root sits a major third under the tonic and the tonic is one of its notes, which is why so distant a chord still sounds related to home.',
    },
  },
  {
    id: 'borrowed-major-fourth',
    movementKind: 'borrowed',
    from: 'key',
    semitones: 5,
    quality: 'major',
    needsKeyMode: 'minor',
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['the major four', 'borrowed IV', 'the subdominant, major'],
      descriptions: [
        'A major subdominant over a minor centre. It brings in a note the key does not own.',
        'Borrowed from the parallel major, and it opens the harmony out without brightening the root.',
        'Airier than the minor key allows. That is the whole reason to reach for it.',
      ],
      characters: ['airy', 'open and cool', 'buoyant'],
      styleTags: ['modal', 'jazz', 'folk'],
      explanation:
        '{to} is the subdominant of {key} with its third raised, borrowed from the parallel major. It {sharedPhrase}, and {newPhrase}. The raised note is the sixth degree of the key, which turns the minor key Dorian for as long as it is sounding.',
    },
  },

  // ---------------------------------------------------------------------------
  // common-tone — held together by a sustained note rather than by function.
  // Every move here is guaranteed to share at least one pitch class with the
  // current chord, from every quality it declares. `library.test.ts` proves it.
  // ---------------------------------------------------------------------------
  {
    id: 'common-tone-diminished-seventh',
    movementKind: 'common-tone',
    from: 'root',
    semitones: 1,
    quality: 'diminished',
    seventh: 'dim7',
    worksFrom: ['major', 'dominant', 'suspended'],
    interpretive: true,
    band: [0.6, 1],
    phrases: {
      relationLabels: [
        'a semitone up, diminished',
        'the held diminished',
        'diminished, one note kept',
      ],
      descriptions: [
        'A diminished seventh a semitone up that keeps notes you are holding and moves each of the others by a semitone.',
        'All the tension of a diminished chord and none of the departure. It hovers in place.',
        'Held together by what stays rather than by where it goes.',
      ],
      characters: ['hovering', 'tense and still', 'shimmering'],
      styleTags: ['romantic', 'cinematic', 'classical'],
      explanation:
        '{to} is a diminished seventh a semitone above {from}, and it is chosen for what it keeps rather than where it leads. It {sharedPhrase}, and {newPhrase}. Return to {from} and the whole thing reads as decoration; go elsewhere and it reads as a pivot.',
    },
  },
  {
    id: 'common-tone-fourth-major-seventh',
    movementKind: 'common-tone',
    from: 'root',
    semitones: 5,
    quality: 'major',
    seventh: 'maj7',
    worksFrom: ['major', 'augmented'],
    band: [0.3, 0.7],
    phrases: {
      relationLabels: ['a fourth up, notes kept', 'the shared-tone fourth', 'held over a fourth'],
      descriptions: [
        'A fourth up with a seventh, keeping two of your notes where they are.',
        'The bass moves and two of the upper notes stay. Luminous, and almost effortless.',
        'A change that barely asks the right hand to move.',
      ],
      characters: ['luminous', 'warm and connected', 'effortless'],
      styleTags: ['jazz', 'ambient', 'lyrical'],
      voicingHint: 'Hold the two shared notes; the bass and one inner voice move.',
      explanation:
        '{to} sits {motion} from {from} and keeps two of its notes in place. It {sharedPhrase}, and {newPhrase}. With that much held, the ear follows the bass line more than the chord change, which is the effect worth using it for.',
    },
  },
  {
    id: 'common-tone-flat-sixth-major-seventh',
    movementKind: 'common-tone',
    from: 'root',
    semitones: 8,
    quality: 'major',
    seventh: 'maj7',
    interpretive: true,
    worksFrom: ['major', 'dominant', 'suspended', 'augmented'],
    band: [0.5, 0.95],
    phrases: {
      relationLabels: ['a minor 6th up, held', 'the glowing sidestep', 'a major 3rd down, maj7'],
      descriptions: [
        'A wide major seventh built around notes you are already holding. Glowing, and distant.',
        'The root travels a long way while the top of the chord barely moves at all.',
        'Warm and far off. It sounds like a change of place rather than a change of chord.',
      ],
      characters: ['glowing', 'wide and warm', 'distant'],
      styleTags: ['ambient', 'cinematic', 'jazz'],
      explanation:
        '{to} is a major seventh {motion} from {from}, and it is built so that what you are holding remains inside it. It {sharedPhrase}, and {newPhrase}. A large root motion under a still upper voice is the cheapest way to make harmony feel like distance.',
    },
  },
  {
    id: 'common-tone-supertonic-seventh',
    movementKind: 'common-tone',
    from: 'root',
    semitones: 2,
    quality: 'minor',
    seventh: 'min7',
    band: [0.2, 0.6],
    phrases: {
      relationLabels: ['a tone up, minor 7th', 'a tone up, minor 7th, held', 'up a tone, held'],
      descriptions: [
        'A minor seventh a whole tone up. It keeps a note of yours and reshapes everything round it.',
        'Gentle and slightly mobile — a chord that reads as passing through rather than arriving.',
        'Neutral in colour, which makes it useful as a hinge between two stronger ideas.',
      ],
      characters: ['neutral', 'mobile', 'soft and passing'],
      styleTags: ['jazz', 'pop', 'lyrical'],
      explanation:
        '{to} is a minor seventh {motion} from {from}, sharing enough with it to feel like a continuation. It {sharedPhrase}, and {newPhrase}. It is a hinge chord: mild on its own, and it makes whatever follows sound deliberate.',
    },
  },
  {
    id: 'common-tone-mediant-seventh',
    movementKind: 'common-tone',
    from: 'root',
    semitones: 4,
    quality: 'minor',
    seventh: 'min7',
    worksFrom: ['major', 'dominant'],
    band: [0.3, 0.75],
    phrases: {
      relationLabels: [
        'a major 3rd up, minor 7th',
        'the shared-tone mediant',
        'the third, extended',
      ],
      descriptions: [
        'A minor seventh a third up, holding two of your notes. Cool, and barely a change at all.',
        'A small move: the harmony tilts rather than travels.',
        'Quietly unresolved. It suits a phrase that is not ready to say where it is going.',
      ],
      characters: ['cool', 'quiet and tilted', 'unresolved'],
      styleTags: ['jazz', 'lyrical', 'ambient'],
      explanation:
        '{to} is a minor seventh {motion} from {from}, and it keeps the upper part of what you are holding. It {sharedPhrase}, and {newPhrase}. With so little moving, this is a change of emphasis rather than of harmony — useful when you want motion without commitment.',
    },
  },
  {
    id: 'common-tone-suspended-fifth',
    movementKind: 'common-tone',
    from: 'root',
    semitones: 7,
    quality: 'sus4',
    band: [0.3, 0.7],
    phrases: {
      relationLabels: ['suspended, a fifth up', 'the held fifth', 'a fifth up, no third'],
      descriptions: [
        'A suspended chord a fifth above. No third at all, so it takes no position on the mood.',
        'Open fourths and fifths, holding notes you already have. Neither bright nor dark.',
        'Bare and spacious. It works as air between two chords that have opinions.',
      ],
      characters: ['bare', 'open and neutral', 'spacious'],
      styleTags: ['ambient', 'folk', 'film'],
      explanation:
        '{to} is a suspended chord {motion} from {from}, with no third to give it a mood. It {sharedPhrase}, and {newPhrase}. Because it declines to be major or minor, whatever you play next decides retrospectively what it was.',
    },
  },

  // ---------------------------------------------------------------------------
  // secondary-dominant — a dominant of something other than the tonic.
  // ---------------------------------------------------------------------------
  {
    id: 'dominant-of-the-fourth',
    movementKind: 'secondary-dominant',
    from: 'root',
    semitones: 0,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['major'],
    impliesKey: { semitones: 5, mode: 'major', certainty: 'suggestive' },
    band: [0.25, 0.7],
    phrases: {
      relationLabels: [
        'same root, as a dominant',
        'dominant of the 4th above',
        'a 7th on this root',
      ],
      descriptions: [
        'The chord you are on, turned into a dominant. It stops resting and starts pointing.',
        'One added note converts a resting chord into a chord with an errand.',
        'The triad stays; a flattened seventh makes it lean toward the fourth.',
      ],
      characters: ['directed', 'bright and urgent', 'leaning'],
      styleTags: ['blues', 'gospel', 'pop'],
      explanation:
        '{to} keeps the triad of {from} and gives it a flattened seventh, which turns it into the dominant of the chord a fourth above. It {sharedPhrase}, and {newPhrase}. The root has not moved, and yet the harmony now has somewhere it means to go.',
    },
  },
  {
    id: 'dominant-of-the-fifth',
    movementKind: 'secondary-dominant',
    from: 'root',
    semitones: 2,
    quality: 'major',
    seventh: 'min7',
    impliesKey: { semitones: 5, mode: 'major', certainty: 'suggestive' },
    band: [0.2, 0.6],
    phrases: {
      relationLabels: ['a tone up, as a 7th', 'dominant of the 5th above', 'V7 of the fifth above'],
      descriptions: [
        'A dominant seventh a tone up. It points at the chord a fifth above where you are.',
        'Bright and directional: it sets up an arrival a fifth above this chord.',
        'One new leading note, put straight to work.',
      ],
      characters: ['directional', 'bright and alert', 'crisp'],
      styleTags: ['jazz', 'classical', 'pop'],
      explanation:
        '{to} is a dominant seventh {motion} from {from}, aimed at the chord a fifth above {from}. It {sharedPhrase}, and {newPhrase}. Take the chord it points to and that chord sounds, for a moment, like home; take anything else and it becomes a colour instead of a signpost.',
    },
  },
  {
    id: 'dominant-of-the-sixth',
    movementKind: 'secondary-dominant',
    from: 'root',
    semitones: 4,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['major', 'dominant', 'suspended'],
    impliesKey: { semitones: 5, mode: 'minor', certainty: 'suggestive' },
    band: [0.3, 0.7],
    phrases: {
      relationLabels: [
        'a major 3rd up, as a 7th',
        'aimed at your relative minor',
        'aimed at the minor 6th above',
      ],
      descriptions: [
        'A dominant seventh a major third up, aimed at the minor chord a sixth above you. Bright over a dark target.',
        'It brings in a leading note that pulls toward your chord’s relative minor.',
        'Yearning rather than resolving. What it points at is darker than what it sounds like.',
      ],
      characters: ['yearning', 'bright and aching', 'poised'],
      styleTags: ['romantic', 'pop', 'classical'],
      explanation:
        '{to} is a dominant seventh {motion} from {from}, and what it points at is the relative minor of {from}, a sixth above it. It {sharedPhrase}, and {newPhrase}. A bright chord aimed at a dark one is why this move can sound like longing.',
    },
  },
  {
    id: 'dominant-of-the-second',
    movementKind: 'secondary-dominant',
    from: 'root',
    semitones: 9,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['major', 'dominant', 'suspended'],
    impliesKey: { semitones: 5, mode: 'minor', certainty: 'suggestive' },
    band: [0.35, 0.75],
    phrases: {
      relationLabels: [
        'a minor 3rd down, as a 7th',
        'dominant of a tone above',
        'V7 of the second above',
      ],
      descriptions: [
        'A dominant seventh a minor third below, pointing at the chord a tone above where you are.',
        'It starts a chain: this chord wants the next one, which wants the one after that.',
        'Sharp and slightly restless, with a leading note of its own.',
      ],
      characters: ['restless', 'sharp and driving', 'insistent'],
      styleTags: ['jazz', 'gospel', 'classical'],
      explanation:
        '{to} is a dominant seventh {motion} from {from}, aimed at the chord a tone above {from}. It {sharedPhrase}, and {newPhrase}. Follow it there and keep falling by fifths, and three chords on the chain is back on {from}.',
    },
  },
  {
    id: 'key-dominant-of-the-fifth',
    movementKind: 'secondary-dominant',
    from: 'key',
    semitones: 2,
    quality: 'major',
    seventh: 'min7',
    needsKeyMode: 'major',
    romanNumeral: 'V7/V',
    impliesKey: { semitones: 5, mode: 'major', certainty: 'clear' },
    band: [0.2, 0.6],
    phrases: {
      relationLabels: ['the dominant of V', 'V7 of the fifth', 'the applied dominant'],
      descriptions: [
        'The dominant of the key’s own dominant. It tips the whole phrase one step sharpward.',
        'A borrowed leading note, pointed at the second-strongest chord in the key.',
        'It sets up an arrival rather than being one. What comes next matters more than this does.',
      ],
      characters: ['directed', 'bright and expectant', 'poised'],
      styleTags: ['classical', 'jazz', 'pop'],
      explanation:
        '{to} is the dominant of the dominant of {key} — a chord borrowed to make one degree of the key feel briefly like home. It {sharedPhrase}, and {newPhrase}. The dominant is the intended next chord; anything else leaves the tilt unresolved, which can be its own effect.',
    },
  },
  {
    id: 'dominant-of-the-minor-fourth',
    movementKind: 'secondary-dominant',
    from: 'root',
    semitones: 0,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['minor'],
    impliesKey: { semitones: 5, mode: 'minor', certainty: 'suggestive' },
    band: [0.35, 0.75],
    phrases: {
      relationLabels: [
        'this root, as a dominant',
        'a 7th on this root',
        'dominant of the 4th above',
      ],
      descriptions: [
        'The same root turned into a dominant, pointing a fourth above.',
        'The third rises, so a minor chord becomes an arrow.',
        'Urgent and bright where a moment ago it was dark.',
      ],
      characters: ['urgent', 'dark and driving', 'sharp'],
      styleTags: ['blues', 'gospel', 'cinematic'],
      explanation:
        '{to} keeps the root of {from} and makes it a dominant seventh, so it now points at the chord a fourth above. It {sharedPhrase}, and {newPhrase}. The root has not moved, so what you hear is the same bass note suddenly acquiring a direction.',
    },
  },

  // ---------------------------------------------------------------------------
  // deceptive — a resolution that lands somewhere other than expected.
  // ---------------------------------------------------------------------------
  {
    id: 'key-deceptive-to-the-sixth',
    movementKind: 'deceptive',
    from: 'key',
    semitones: 9,
    quality: 'minor',
    needsKeyMode: 'major',
    avoidAfterKinds: ['deceptive'],
    band: [0.25, 0.65],
    phrases: {
      relationLabels: ['the relative minor (vi)', 'the sixth degree', 'landing on vi'],
      descriptions: [
        'The key’s relative minor. After a dominant it is the classic evasion; anywhere else, a gentle turn to the dark side of the key.',
        'Two notes of the tonic chord with the floor dropped a third. It can stand where home would go without being home.',
        'A soft, continuing chord. It keeps momentum by not closing.',
      ],
      characters: ['soft and sidelong', 'gentle', 'continuing'],
      styleTags: ['classical', 'pop', 'lyrical'],
      explanation:
        '{to} is the relative minor of {key}. It {sharedPhrase}, and {newPhrase}. Two of its notes are the tonic chord’s, which is why after a dominant it passes as an arrival rather than a wrong turn.',
    },
  },
  {
    id: 'key-deceptive-to-the-sixth-major',
    movementKind: 'deceptive',
    from: 'key',
    semitones: 8,
    quality: 'major',
    needsKeyMode: 'minor',
    romanNumeral: 'VI',
    band: [0.25, 0.7],
    phrases: {
      relationLabels: ['the sixth degree, VI', 'the wide sixth', 'landing on VI'],
      descriptions: [
        'The major chord on the sixth degree of a minor key. After a dominant it stands in for home; anywhere else it simply widens the room.',
        'A wide major chord a major third below the tonic, sharing two of its notes. Broad, warm, unfinished.',
        'It opens rather than closes. The phrase gets longer, not shorter.',
      ],
      characters: ['broad', 'warm and unfinished', 'expansive'],
      styleTags: ['classical', 'cinematic', 'romantic'],
      explanation:
        '{to} is the major chord on the sixth degree of {key}. It {sharedPhrase}, and {newPhrase}. It shares two notes with the tonic chord, so after a dominant it satisfies the ear while withholding the close.',
    },
  },
  {
    id: 'deceptive-landing-minor',
    movementKind: 'deceptive',
    from: 'root',
    semitones: 2,
    quality: 'minor',
    worksFrom: ['major', 'dominant', 'suspended', 'augmented'],
    avoidAfterKinds: ['deceptive'],
    band: [0.3, 0.7],
    phrases: {
      relationLabels: ['a tone up, into minor', 'sidestep into minor', 'a tone up, minor'],
      descriptions: [
        'A tone up, into minor. After a dominant it is the classic sidestep; from anywhere else, a soft lift.',
        'Mellow and open: it keeps the phrase going rather than closing it.',
        'Enough of the chord a fifth below survives in it to sound intended.',
      ],
      characters: ['mellow', 'soft and sidelong', 'continuing'],
      styleTags: ['jazz', 'pop', 'lyrical'],
      explanation:
        '{to} is {motion} from {from} and minor. It {sharedPhrase}, and {newPhrase}. If {from} was pulling down a fifth, this is where the resolution goes when it declines to arrive: it holds two of the three notes that chord would have had, so the ear accepts it and the phrase stays open.',
    },
  },
  {
    id: 'deceptive-landing-flat-sixth',
    movementKind: 'deceptive',
    from: 'root',
    semitones: 1,
    quality: 'major',
    worksFrom: ['major', 'dominant', 'suspended', 'augmented'],
    band: [0.4, 0.85],
    phrases: {
      relationLabels: ['a semitone up, major', 'a semitone up instead', 'sidestep upward'],
      descriptions: [
        'A semitone up into a major chord. After a dominant the resolution goes up a semitone instead of down a fifth: wide, and wrong-footed.',
        'A large chord in a place nothing pointed at, a semitone above where you are.',
        'Broad and abrupt. It lands somewhere the ear was not looking.',
      ],
      characters: ['abrupt and wide', 'bold', 'wrong-footed'],
      styleTags: ['cinematic', 'romantic', 'film'],
      explanation:
        '{to} arrives a semitone above {from}. It {sharedPhrase}, and {newPhrase}. If {from} was pulling down a fifth, the gap between what was implied and what arrived is the whole effect; without that pull it is a bold sidestep rather than a deception.',
    },
  },
  {
    id: 'deceptive-landing-fourth-major-seventh',
    movementKind: 'deceptive',
    from: 'root',
    semitones: 10,
    quality: 'major',
    seventh: 'maj7',
    worksFrom: ['dominant', 'suspended'],
    band: [0.45, 0.85],
    phrases: {
      relationLabels: [
        'falls to a major 7th',
        'a whole tone down, maj7',
        'a whole tone down, soft',
      ],
      descriptions: [
        'The tension falls a whole tone into a major seventh rather than resolving properly.',
        'A plush, blurred landing. It answers the question without closing the subject.',
        'Warm and slightly out of focus, which is why the phrase can continue straight through it.',
      ],
      characters: ['plush', 'warm and blurred', 'unclosed'],
      styleTags: ['jazz', 'soul', 'film'],
      explanation:
        '{to} is a major seventh {motion} from {from}, taking the place of the expected resolution. It {sharedPhrase}, and {newPhrase}. Because a major seventh is never quite a full stop, the deception is doubled: wrong chord, and it does not settle either.',
    },
  },
  {
    id: 'deceptive-to-the-sixth-from-minor',
    movementKind: 'deceptive',
    from: 'root',
    semitones: 8,
    quality: 'major',
    worksFrom: ['minor', 'diminished'],
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['a major 3rd down, major', 'the wide sidestep', 'the major chord below'],
      descriptions: [
        'Two of your notes stay put and the floor drops a major third. Warm, and wider.',
        'A broad major chord under notes you are already holding. It opens the phrase out.',
        'The same moment, widened: nothing you hold has to move far.',
      ],
      characters: ['wide', 'warm and open', 'unfinished'],
      styleTags: ['cinematic', 'romantic', 'film'],
      explanation:
        '{to} is the major chord {motion} from {from}. It {sharedPhrase}, and {newPhrase}. Keeping two notes while the bass drops a third is why it sounds like the same moment widening rather than a new one.',
    },
  },

  // ---------------------------------------------------------------------------
  // planing — the same shape moved bodily, function suspended.
  // The target copies the current chord's own quality, so what changes is the
  // pitch and nothing else. All interpretive: this is a way of hearing, not a rule.
  // ---------------------------------------------------------------------------
  {
    id: 'plane-up-a-semitone',
    movementKind: 'planing',
    from: 'root',
    semitones: 1,
    quality: 'same',
    avoidAfterKinds: ['planing'],
    interpretive: true,
    band: [0.6, 1],
    phrases: {
      relationLabels: ['the same shape, up a 1/2', 'slide up a semitone', 'planed up'],
      descriptions: [
        'The whole shape slides up one semitone, every voice together.',
        'Nothing is reinterpreted. The hand keeps its shape and the harmony goes somewhere else.',
        'Electric and slightly unreal: every voice moves by the same half step.',
      ],
      characters: ['sliding', 'bright and unreal', 'electric'],
      styleTags: ['film', 'experimental', 'ambient'],
      explanation:
        '{to} is {from} moved bodily up a semitone — the same chord type, a new pitch. It {sharedPhrase}, and {newPhrase}. The ear follows the parallel slide before it hears any function, so the effect depends on the shape being recognisable as the same shape.',
    },
  },
  {
    id: 'plane-down-a-semitone',
    movementKind: 'planing',
    from: 'root',
    semitones: 11,
    quality: 'same',
    avoidAfterKinds: ['planing'],
    interpretive: true,
    band: [0.6, 1],
    phrases: {
      relationLabels: ['the same shape, down a 1/2', 'slide down a semitone', 'planed down'],
      descriptions: [
        'The same shape a semitone lower; every voice slips down together.',
        'Shadowed and floating: the harmony drops and the hand keeps its shape.',
        'A slip rather than a step. Everything moves together.',
      ],
      characters: ['sinking', 'shadowed', 'floating'],
      styleTags: ['film', 'ambient', 'cinematic'],
      explanation:
        '{to} is {from} moved bodily down a semitone, the same chord type. It {sharedPhrase}, and {newPhrase}. The ear tracks the parallel movement first; whether it also lands somewhere depends on what the harmony around it has been doing.',
    },
  },
  {
    id: 'plane-up-a-tone',
    movementKind: 'planing',
    from: 'root',
    semitones: 2,
    quality: 'same',
    interpretive: true,
    band: [0.55, 1],
    phrases: {
      relationLabels: ['the same shape, up a tone', 'slide up a whole tone', 'planed up a tone'],
      descriptions: [
        'The shape moves up a whole tone. Bright and weightless.',
        'A clean parallel shift, heard as a lift.',
        'Open and slightly synthetic, in the way that parallel motion always is.',
      ],
      characters: ['weightless', 'clean and lifted', 'open'],
      styleTags: ['film', 'ambient', 'experimental'],
      explanation:
        '{to} is {from} moved up a whole tone, the same chord type. It {sharedPhrase}, and {newPhrase}. The ear hears the parallel lift first, and only then whatever function the new chord happens to have.',
    },
  },
  {
    id: 'plane-down-a-tone',
    movementKind: 'planing',
    from: 'root',
    semitones: 10,
    quality: 'same',
    interpretive: true,
    band: [0.55, 1],
    phrases: {
      relationLabels: [
        'the same shape, down a tone',
        'slide down a whole tone',
        'planed down a tone',
      ],
      descriptions: [
        'The shape drops a whole tone, unchanged. Cool and wide.',
        'It sinks by a step and takes the harmony with it, without reinterpreting anything.',
        'Neutral in colour: nothing is reinterpreted, everything just moves.',
      ],
      characters: ['cool', 'wide and neutral', 'drifting'],
      styleTags: ['ambient', 'film', 'modal'],
      explanation:
        '{to} is {from} moved down a whole tone, the same chord type. It {sharedPhrase}, and {newPhrase}. What the ear follows is the parallel motion; the harmony is a by-product of it, which is the whole idea.',
    },
  },
  {
    id: 'plane-up-a-fourth',
    movementKind: 'planing',
    from: 'root',
    semitones: 5,
    quality: 'same',
    interpretive: true,
    band: [0.45, 0.9],
    phrases: {
      relationLabels: ['the same shape, up a 4th', 'slide up a fourth', 'planed up a fourth'],
      descriptions: [
        'The shape moves up a fourth, unchanged.',
        'A step the ear knows, with the shape carried across unchanged.',
        'Solid and slightly stubborn, because the shape will not bend.',
      ],
      characters: ['solid', 'plain and blunt', 'stubborn'],
      styleTags: ['rock', 'modal', 'film'],
      explanation:
        '{to} is {from} moved up a fourth, the same chord type. It {sharedPhrase}, and {newPhrase}. Up a fourth is the most ordinary root motion there is, so the effect is a familiar step taken without bending the shape.',
    },
  },
  {
    id: 'plane-a-tritone',
    movementKind: 'planing',
    from: 'root',
    semitones: 6,
    quality: 'same',
    interpretive: true,
    worksFrom: ['major', 'minor', 'suspended', 'augmented'],
    band: [0.7, 1],
    phrases: {
      relationLabels: ['the same shape, a tritone', 'slide by a tritone', 'planed a tritone'],
      descriptions: [
        'The shape moves by a tritone — the furthest it can go and still be the same shape.',
        'As far from where you are as the twelve notes allow, and played as one gesture.',
        'Strange, and as remote as harmony gets. Use it as a cut, not a step.',
      ],
      characters: ['strange', 'remote and stark', 'severed'],
      styleTags: ['experimental', 'film', 'ambient'],
      explanation:
        '{to} is {from} moved by a tritone, the largest distance any move can travel. It {sharedPhrase}, and {newPhrase}. Little connects the two chords beyond the shape they share, so this reads as an edit rather than a progression.',
    },
  },

  // ---------------------------------------------------------------------------
  // distant-modulation — a pivot to a genuinely remote key.
  // ---------------------------------------------------------------------------
  {
    id: 'tritone-away-major-seventh',
    movementKind: 'distant-modulation',
    from: 'root',
    semitones: 6,
    quality: 'major',
    seventh: 'maj7',
    interpretive: true,
    impliesKey: { semitones: 0, mode: 'major', certainty: 'clear' },
    worksFrom: ['major', 'minor', 'dominant', 'suspended', 'augmented'],
    band: [0.75, 1],
    phrases: {
      relationLabels: ['a tritone away', 'the far major 7th', 'the opposite pole'],
      descriptions: [
        'A major seventh a tritone away — the furthest point from where you are, arriving as a place.',
        'Not a colour but a relocation, as far from where you are as the twelve notes allow.',
        'Remote and luminous. It works by being unarguable.',
      ],
      characters: ['remote', 'luminous and far', 'severed'],
      styleTags: ['cinematic', 'film', 'experimental'],
      explanation:
        '{to} is a major seventh a tritone from {from}, as far as a root can travel in twelve notes. It {sharedPhrase}, and {newPhrase}. Treat it as an arrival: give it a bar to settle before moving again.',
    },
  },
  {
    id: 'key-tritone-away',
    movementKind: 'distant-modulation',
    from: 'key',
    semitones: 6,
    quality: 'major',
    needsKey: true,
    interpretive: true,
    impliesKey: { semitones: 0, mode: 'major', certainty: 'clear' },
    band: [0.8, 1],
    phrases: {
      relationLabels: ['a tritone from the key', 'the far key', 'the opposite tonic'],
      descriptions: [
        'A plain major chord as far from the key as it is possible to be. It simply replaces it.',
        'The old tonic and this one share almost nothing. That is the entire argument for it.',
        'Bare and remote. Nothing eases the transition, and nothing needs to.',
      ],
      characters: ['bare and remote', 'stark', 'far'],
      styleTags: ['film', 'experimental', 'cinematic'],
      explanation:
        '{to} is the major chord a tritone from the tonic of {key} — the most distant key there is. It {sharedPhrase}, and {newPhrase}. It is an assertion rather than a modulation, so what makes it work is committing to it for long enough to be believed.',
    },
  },
  {
    id: 'key-semitone-up-major-seventh',
    movementKind: 'distant-modulation',
    from: 'key',
    semitones: 1,
    quality: 'major',
    seventh: 'maj7',
    needsKey: true,
    interpretive: true,
    impliesKey: { semitones: 0, mode: 'major', certainty: 'clear' },
    band: [0.75, 1],
    phrases: {
      relationLabels: ['a semitone above the key', 'the key, lifted', 'up a semitone, far'],
      descriptions: [
        'The whole key lifted by a semitone, arriving as a major seventh. Close, and completely elsewhere.',
        'The smallest step on the keyboard, taken as a change of key rather than as a neighbour.',
        'Glassy and sudden, a key change at the smallest possible distance.',
      ],
      characters: ['glassy', 'close and far', 'sudden'],
      styleTags: ['film', 'pop', 'cinematic'],
      explanation:
        '{to} sits a semitone above the tonic of {key} and behaves as a new tonic rather than as a neighbour. It {sharedPhrase}, and {newPhrase}. A semitone is the smallest distance on the keyboard, which is exactly what makes treating it as a new key such a jolt.',
    },
  },
  {
    id: 'key-major-third-up-major-seventh',
    movementKind: 'distant-modulation',
    from: 'key',
    semitones: 4,
    quality: 'major',
    seventh: 'maj7',
    needsKey: true,
    needsKeyMode: 'major',
    interpretive: true,
    impliesKey: { semitones: 0, mode: 'major', certainty: 'clear' },
    band: [0.7, 1],
    phrases: {
      relationLabels: ['a major 3rd above the key', 'the third, as a key', 'up a third, far'],
      descriptions: [
        'A major seventh a third above the key’s tonic, taken as a new centre rather than a colour.',
        'Third-related modulation: far enough to be a journey, near enough to be followed.',
        'Bright and clean. The old key is still audible behind it for a bar or so.',
      ],
      characters: ['bright and far', 'clean', 'travelling'],
      styleTags: ['romantic', 'cinematic', 'film'],
      explanation:
        '{to} is a major seventh a third above the tonic of {key}, and it is offered as a destination rather than as a passing colour. It {sharedPhrase}, and {newPhrase}. Third-related keys share just enough to make the move readable, which is why the nineteenth century used them constantly.',
    },
  },
  {
    id: 'key-flat-sixth-major-seventh',
    movementKind: 'distant-modulation',
    from: 'key',
    semitones: 8,
    quality: 'major',
    seventh: 'maj7',
    needsKey: true,
    needsKeyMode: 'major',
    interpretive: true,
    impliesKey: { semitones: 0, mode: 'major', certainty: 'clear' },
    band: [0.65, 1],
    phrases: {
      relationLabels: ['the flat sixth, as a key', 'a minor 6th above', 'the wide key'],
      descriptions: [
        'The flattened sixth taken as a new tonic. Velvet, broad, and a long way from home.',
        'A key change that sounds like an opening rather than a departure.',
        'Warm and wide. Of the remote keys, this is the one that arrives most gently.',
      ],
      characters: ['velvet', 'wide and far', 'opening'],
      styleTags: ['cinematic', 'romantic', 'film'],
      explanation:
        '{to} takes the flattened sixth of {key} as a tonic in its own right. It {sharedPhrase}, and {newPhrase}. It is the gentlest of the remote modulations because the chord itself is already borrowable, so the ear has heard it before it is asked to live there.',
    },
  },

  // ---------------------------------------------------------------------------
  // stylistic-idiom — moves that belong to a particular repertoire.
  // ---------------------------------------------------------------------------
  {
    id: 'tritone-substitute-dominant',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 1,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['major', 'minor', 'suspended', 'diminished'],
    interpretive: true,
    band: [0.55, 0.95],
    phrases: {
      relationLabels: [
        'the tritone substitute',
        'a semitone above, as a 7th',
        'the flat second 7th',
      ],
      descriptions: [
        'A dominant seventh a semitone up: the tritone substitute for this chord’s own dominant.',
        'A jazz substitution: the two notes that do the work stay, and the floor moves under them.',
        'Smoky and sideways. It wants to fall straight back down a semitone to where you are.',
      ],
      characters: ['smoky', 'sideways', 'sleek and tense'],
      styleTags: ['jazz', 'noir', 'film'],
      ambiguityNote: 'Shares its tritone with the ordinary dominant, so both readings are correct.',
      explanation:
        '{to} is the tritone substitute for the dominant of {from}: the same tritone, a different root. It {sharedPhrase}, and {newPhrase}. Its bass wants to fall a semitone back into {from}, so going there and back turns a plain neighbour into a slide.',
    },
  },
  {
    id: 'tritone-substitute-of-this',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 6,
    quality: 'major',
    seventh: 'min7',
    worksFrom: ['dominant'],
    interpretive: true,
    band: [0.6, 1],
    phrases: {
      relationLabels: ['this chord, substituted', 'the tritone swap', 'a tritone away, as a 7th'],
      descriptions: [
        'The same tension, re-rooted a tritone away: the two notes that drive it stay, the rest move.',
        'Two chords, one tritone. Everything that made the last chord move is still moving.',
        'Slick and disorienting, and it resolves in exactly the same place.',
      ],
      characters: ['slick', 'sideways', 'tense and cool'],
      styleTags: ['jazz', 'noir', 'film'],
      explanation:
        '{to} shares with {from} the tritone that drives it, re-rooted a tritone away. It {sharedPhrase}, and {newPhrase}. Both chords lean toward the same resolution, so this changes the road rather than the destination.',
    },
  },
  {
    id: 'neapolitan-sixth',
    movementKind: 'stylistic-idiom',
    from: 'key',
    semitones: 1,
    quality: 'major',
    bassSemitones: 4,
    needsKey: true,
    romanNumeral: 'N6',
    band: [0.6, 1],
    phrases: {
      relationLabels: ['the Neapolitan', 'flat second, first inversion', 'the Neapolitan sixth'],
      descriptions: [
        'The major chord on the flattened second, with its third in the bass. Poised and antique.',
        'An old colour: bittersweet, formal, and always heading toward the dominant.',
        'It leans down onto the key from a semitone above, which is why it aches.',
      ],
      characters: ['poised', 'bittersweet', 'antique'],
      styleTags: ['classical', 'romantic', 'film'],
      voicingHint:
        'Put the third in the bass — root position turns it into an ordinary flat second.',
      explanation:
        '{to} is the major chord on the flattened second degree of {key}, played with its third in the bass. It {sharedPhrase}, and {newPhrase}. Convention sends it to the dominant next. Its root falls a diminished third to the leading note, or a semitone onto the tonic, and that downward lean is where the whole effect lives.',
    },
  },
  {
    id: 'backdoor-dominant',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 10,
    quality: 'major',
    seventh: 'min7',
    // Not from a minor chord: G7 → Am is not the back-door cadence, which is ♭VII7 into a
    // *major* I. Over A minor, G7 reads as the dominant of C, and the label and the
    // description would both be claiming a destination it does not have.
    worksFrom: ['major', 'suspended'],
    band: [0.5, 0.9],
    phrases: {
      relationLabels: [
        'the back-door seventh',
        'a whole tone below, as a 7th',
        'the flat seventh 7th',
      ],
      descriptions: [
        'A dominant seventh a whole tone below, resolving upward instead of down. Warm and oblique.',
        'It arrives at the same place as the ordinary dominant by walking round the back.',
        'Soulful rather than urgent, because the pull is a step rather than a leap.',
      ],
      characters: ['oblique', 'warm and soulful', 'rounded'],
      styleTags: ['jazz', 'soul', 'gospel'],
      explanation:
        '{to} is a dominant seventh {motion} from {from}, and it resolves upward by a tone rather than downward by a fifth. It {sharedPhrase}, and {newPhrase}. Coming back to {from} after it is a soft, rising resolution, a step up rather than a fall of a fifth, and it lands gentler than a real dominant would.',
    },
  },
  {
    id: 'suspended-in-place',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 0,
    quality: 'sus4',
    // Triads only in spirit: a dominant's seventh would be dropped by a plain sus4, and a
    // sus normally resolves *into* the third, so suspending G7 at the cadence point undoes
    // the pull the phrase just built. A major-seventh chord still reads as 'major' here, so
    // the labels name the suspension rather than promising "the same chord".
    worksFrom: ['major', 'minor'],
    band: [0.3, 0.7],
    phrases: {
      relationLabels: ['this root, suspended', 'third becomes a fourth', 'suspended in place'],
      descriptions: [
        'The third lifts to a fourth and the chord stops having a mood.',
        'A suspension on the spot: same root, same fifth, and the middle voice held above itself.',
        'Open and unresolved, and it falls back into place whenever you want it to.',
      ],
      characters: ['open', 'held', 'unresolved'],
      styleTags: ['pop', 'folk', 'gospel'],
      explanation:
        '{to} replaces the third of {from} with a fourth, keeping the root and fifth. It {sharedPhrase}, and {newPhrase}. With no third it takes no position on major or minor, so falling back to {from} always sounds like an answer.',
    },
  },
  {
    // The suspension a dominant actually takes: the seventh stays, so the pull stays, and
    // only the arrival is put off. What `suspended-in-place` would have done to G7 had it
    // kept accepting one — dropped the seventh under a label promising the same chord.
    id: 'suspended-seventh-in-place',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 0,
    quality: 'sus4',
    seventh: 'min7',
    worksFrom: ['dominant'],
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['this 7th, suspended', 'the 7sus4', 'held off the third'],
      descriptions: [
        'The same seventh chord with its third lifted to a fourth. The pull stays; the arrival waits.',
        'Root, fourth, fifth and seventh: a dominant that has not decided to land yet.',
        'Gospel and soul hold a cadence here before letting the third back in.',
      ],
      characters: ['poised', 'held and warm', 'suspended'],
      styleTags: ['gospel', 'soul', 'pop'],
      explanation:
        '{to} keeps the root, fifth and seventh of {from} and lifts its third to a fourth. It {sharedPhrase}, and {newPhrase}. The seventh keeps the pull, so letting the fourth fall back to the third and then moving on is the usual way out: the cadence delayed, not cancelled.',
    },
  },
  {
    id: 'added-ninth-in-place',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 0,
    quality: 'major',
    extensions: ['9'],
    worksFrom: ['major'],
    band: [0.25, 0.65],
    phrases: {
      relationLabels: ['the same chord, plus a 9th', 'an added ninth', 'this chord, opened'],
      descriptions: [
        'The same root and triad with a ninth on top. The harmony just gets more air in it.',
        'One extra note, high and unresolved, and the chord stops sounding like a full stop.',
        'Bright and slightly ringing. A small change that makes a plain chord sound considered.',
      ],
      characters: ['ringing', 'bright and open', 'airy'],
      styleTags: ['pop', 'folk', 'ambient'],
      explanation:
        '{to} keeps the triad of {from} and puts a ninth above it. It {sharedPhrase}, and {newPhrase}. The added note sits a tone from the root, so it rings against it rather than resolving, which is what stops the chord sounding closed.',
    },
  },
  {
    id: 'six-nine-in-place',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 0,
    quality: 'major',
    extensions: ['6', '9'],
    worksFrom: ['major'],
    band: [0.4, 0.85],
    phrases: {
      relationLabels: ['the same chord, six-nine', 'a sixth and a ninth', 'opened out in place'],
      descriptions: [
        'The same root with a sixth and a ninth on it. Wide, glassy, and settled.',
        'Five notes that sound like one colour, and none of them pulls anywhere.',
        'Plush and open. A major chord that sounds finished without sounding final.',
      ],
      characters: ['plush', 'wide and settled', 'glassy'],
      styleTags: ['jazz', 'soul', 'film'],
      explanation:
        '{to} keeps the triad of {from} and adds a sixth and a ninth above it. It {sharedPhrase}, and {newPhrase}. Neither added note pulls anywhere, so the chord sits still while sounding richer, which is why it so often ends a phrase without shutting the door.',
    },
  },
  {
    id: 'descending-bass-major-seventh',
    movementKind: 'stylistic-idiom',
    from: 'root',
    semitones: 0,
    quality: 'major',
    seventh: 'maj7',
    bassSemitones: 11,
    worksFrom: ['major'],
    band: [0.4, 0.85],
    phrases: {
      relationLabels: ['same chord, bass steps down', 'the line-cliché', 'a 7th in the bass'],
      descriptions: [
        'The same chord with its major seventh in the bass, a semitone under the root. The start of a descending line.',
        'The floor begins to walk while the chord above it holds. Lyrical and slightly melancholy.',
        'A held chord over a moving bass — the oldest trick in songwriting, and still the best.',
      ],
      characters: ['lyrical', 'walking', 'gently melancholy'],
      styleTags: ['pop', 'lyrical', 'film'],
      voicingHint: 'Keep the right hand still and walk the bass down from here.',
      explanation:
        '{to} is {from} with a major seventh placed in the bass, a semitone below the root. It {sharedPhrase}, and {newPhrase}. It is the first step of a descending bass line, so the chord after it usually keeps walking rather than jumping.',
    },
  },
  {
    id: 'picardy-third',
    movementKind: 'stylistic-idiom',
    from: 'key',
    semitones: 0,
    quality: 'major',
    needsKeyMode: 'minor',
    band: [0.3, 0.75],
    phrases: {
      relationLabels: ['the Picardy third', 'minor ends major', 'the third, raised'],
      descriptions: [
        'A minor phrase ending on a major chord. Old, formal, and unreasonably moving.',
        'The home chord, with its third raised at the last moment.',
        'Consolation rather than triumph. It works because it arrives at the end, not before it.',
      ],
      characters: ['consoling', 'bright and formal', 'resolved'],
      styleTags: ['classical', 'romantic', 'choral'],
      explanation:
        '{to} is the tonic of {key} with its third raised, so a minor passage ends major. This is the Picardy third, in use since long before it had a name. It {sharedPhrase}, and {newPhrase}. It belongs at the end of something; used in the middle it reads as an ordinary change of mode.',
    },
  },
]);

/** Every move, indexed by `id`. Built once — the library is frozen data. */
export const MOVES_BY_ID: ReadonlyMap<string, HeuristicMove> = new Map(
  MOVE_LIBRARY.map((move) => [move.id, move]),
);

/** How many moves the library holds. Exported so callers do not count by hand. */
export const MOVE_LIBRARY_SIZE = MOVE_LIBRARY.length;
